import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BellRinging, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import API from "../../lib/api";
import { Button } from "../ui/button";
import { money } from "../../lib/resellerFormat";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "—");

/** "Who's due a payout" card + the manual trigger for the 1st-of-month reminder e-mail. */
export function PayoutReminderCard() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => API.get("/platform/resellers/payout-reminder").then(({ data }) => setS(data)).catch(() => setS({ due: [], due_total: 0 })), []);
  useEffect(() => { load(); }, [load]);
  const send = async () => {
    setBusy(true);
    try { const { data } = await API.post("/platform/resellers/payout-reminder/run-now"); toast.success(data.message); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't send the reminder"); }
    setBusy(false);
  };
  if (!s) return null;
  const due = s.due || [];
  return (
    <section className={`rounded-xl border p-4 ${due.length ? "border-amber-300 bg-amber-50 dark:bg-amber-950/20" : "border-border bg-card"}`} data-testid="payout-reminder-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-bold font-[Chivo] text-sm flex items-center gap-2"><BellRinging size={16} weight="duotone" className={due.length ? "text-amber-600" : "text-muted-foreground"} /> Payouts due</h2>
          {due.length ? (
            <p className="text-sm mt-1" data-testid="payout-due-summary"><b>{money(s.due_total)}</b> to transfer across {due.length} reseller{due.length === 1 ? "" : "s"} for closed months.</p>
          ) : (
            <p className="text-sm text-muted-foreground mt-1" data-testid="payout-due-summary">Nothing outstanding — every closed month is marked paid.</p>
          )}
          <p className="text-[11px] text-muted-foreground mt-1" data-testid="payout-reminder-last">
            Platform admins get this list by e-mail and in-app on the 1st of each month.{s.last?.sent_at ? ` Last reminder ${fmt(s.last.sent_at)}${s.last.resellers ? ` (${s.last.resellers} due)` : " (nothing was due)"}.` : " No reminder sent yet."}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={send} disabled={busy} data-testid="payout-reminder-send"><PaperPlaneTilt size={14} className="mr-1" /> {busy ? "Sending…" : "Send reminder now"}</Button>
      </div>
      {due.length > 0 && (
        <ul className="mt-3 divide-y divide-amber-200/60 dark:divide-amber-900/40 text-sm" data-testid="payout-due-list">
          {due.map((d) => (
            <li key={d.id} className="py-2 flex flex-wrap items-center justify-between gap-2" data-testid={`payout-due-${d.id}`}>
              <div><Link to={`/platform/resellers/${d.id}`} className="font-medium hover:underline">{d.name}</Link><span className="text-xs text-muted-foreground ml-2">{d.months.map((m) => m.label).join(", ")}</span>{d.payout_details && <p className="text-[11px] text-muted-foreground">Pay to: {d.payout_details}</p>}</div>
              <b>{money(d.total)}</b>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
